import {
  Modal,
  PrimaryBlueButton,
  PrimaryGrayButton,
  Toggle,
  InfoCircle,
  CustomizableSelect,
  Severity
} from "@velaro/velaro-component-lib";
import React, { useState } from "react";
import AIChatbotSettingsRow from "./AIChatbotSettingsRow";
import { WebTrainingDetails } from "../types";
import AIChatbotSettingsRowToggle from "./AIChatbotSettingsRowToggle";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";

interface Props {
  urls: string[];
  clearTrainingModal(): void;
  webCrawl(details: WebTrainingDetails): void;
}

export default function AICrawlingModal(props: Props) {
  const [trainingDetails, setTrainingDetails] = useState<WebTrainingDetails>({
    name: "",
    urls: props.urls,
    crawlDepth: 1,
    maxPages: 25,
    includeExternal: false,
    excludedCssSelectors: "",
    aiCrawl: false,
    aiCrawlPrompt: "",
    crawlResultDelay: 1,
    crawlUrlExclusion: "",
    crawlUrlFilter: ""
  });
  const [showAdvancedFeatures, setShowAdvancedFeatures] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const toastAlert = useToastAlert();
  const subscription = useSubscriptions();

  let angleClassName = "ml-1 transition fa-solid fa-angle-down";
  let bgClass =
    "bg-blue-50 dark:bg-stone-400 dark:text-white rounded-t hover:bg-blue-100";
  if (showAdvancedFeatures) {
    angleClassName += " rotate-180";
    bgClass = "bg-blue-50 dark:bg-stone-400 dark:text-white hover:bg-blue-100";
  }

  const maxPages = [
    {
      label: "25 Pages",
      value: 25,
      action: () => {
        setTrainingDetails({
          ...trainingDetails,
          maxPages: 25
        });
      }
    },
    {
      label: "50 Pages",
      value: 50,
      action: () => {
        setTrainingDetails({
          ...trainingDetails,
          maxPages: 50
        });
      }
    },
    {
      label: "100 Pages",
      value: 100,
      action: () => {
        setTrainingDetails({
          ...trainingDetails,
          maxPages: 100
        });
      }
    },
    {
      label: "200 Pages",
      value: 200,
      action: () => {
        setTrainingDetails({
          ...trainingDetails,
          maxPages: 200
        });
      }
    }
  ];

  function runCrawler(crawlerDetails: WebTrainingDetails) {
    if (crawlerDetails.name.trim().length === 0) {
      toastAlert.displayToast(
        Severity.Error,
        "Please enter a name for the crawl"
      );
      return;
    }
    setDisabled(true);
    props.webCrawl(crawlerDetails);
    props.clearTrainingModal();
  }

  return (
    <Modal maxWidth="max-w-2xl" show={true}>
      <div className="bg-white p-4 rounded-2xl w-full self-center mt-2">
        <div className="font-semibold text-lg pb-1">Crawler Settings</div>
        <div className="">
          <AIChatbotSettingsRow
            title="Name"
            subtitle="A unique name for the training instance"
            input={
              <input
                value={trainingDetails.name || ""}
                onChange={(e) => {
                  setTrainingDetails({
                    ...trainingDetails!,
                    name: e.target.value
                  });
                }}
                type="text"
                placeholder={""}
                className="w-full p-2 border-[1px] rounded-md text-sm"
              />
            }
          />
          <AIChatbotSettingsRow
            title="Crawl Depth"
            subtitle="Depth of Web Crawl (Max: 5)"
            input={
              <>
                <input
                  value={trainingDetails.crawlDepth}
                  onChange={(e) => {
                    setTrainingDetails({
                      ...trainingDetails!,
                      crawlDepth: e.target.valueAsNumber
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key) {
                      e.preventDefault();
                      return;
                    }
                  }}
                  type="number"
                  min="1"
                  max="5"
                  placeholder={""}
                  className="w-full p-2 border-[1px] rounded-md text-sm"
                />
              </>
            }
          />
          <div className="flex flex-col w-1/2 pr-3">
            <div className="font-semibold text-sm pb-1 flex flex-row ">
              Max Pages
            </div>
            <div className="text-slate-500 text-sm pb-2">
              Maximum number of pages to crawl
            </div>
            <div className="text-sm pb-1 flex flex-row ">
              <CustomizableSelect
                options={maxPages}
                defaultOption={maxPages[0]}
              />
            </div>
          </div>
          <div className="flex flex-row">
            <button
              className={`flex flex-row items-center justify-center"`}
              onClick={() => setShowAdvancedFeatures(!showAdvancedFeatures)}
            >
              <div className="text-blue-500 text-xs font-semibold font-['Rubik'] leading-none mt-2">
                Advanced Settings
                <i className={angleClassName}></i>
              </div>
            </button>
          </div>
          {showAdvancedFeatures && (
            <>
              <AIChatbotSettingsRowToggle
                title="Include External Links"
                subtitle="Crawls external links when found"
                input={
                  <Toggle
                    toggled={trainingDetails.includeExternal}
                    changeToggle={() =>
                      setTrainingDetails({
                        ...trainingDetails!,
                        includeExternal: !trainingDetails.includeExternal
                      })
                    }
                  />
                }
              />
              <AIChatbotSettingsRow
                title="Excluded CSS Selectors"
                subtitle="Input CSS selectors to exclude from the crawl"
                input={
                  <input
                    value={trainingDetails.excludedCssSelectors || ""}
                    onChange={(e) => {
                      setTrainingDetails({
                        ...trainingDetails!,
                        excludedCssSelectors: e.target.value
                      });
                    }}
                    type="text"
                    placeholder={""}
                    className="w-full p-2 border-[1px] rounded-md text-sm"
                  />
                }
              />
              <AIChatbotSettingsRow
                title="Crawl Result Delay"
                subtitle="Delay of HTML Result for Page Loading (Max: 5)"
                input={
                  <>
                    <input
                      value={trainingDetails.crawlResultDelay}
                      onChange={(e) => {
                        setTrainingDetails({
                          ...trainingDetails!,
                          crawlResultDelay: e.target.valueAsNumber
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key) {
                          e.preventDefault();
                          return;
                        }
                      }}
                      type="number"
                      min="1"
                      max="5"
                      placeholder={""}
                      className="w-full p-2 border-[1px] rounded-md text-sm"
                    />
                  </>
                }
              />
              <AIChatbotSettingsRow
                title="URL Exclusion"
                subtitle="Input URL patterns to exclude from the crawl"
                input={
                  <input
                    value={trainingDetails.crawlUrlExclusion || ""}
                    onChange={(e) => {
                      setTrainingDetails({
                        ...trainingDetails!,
                        crawlUrlExclusion: e.target.value
                      });
                    }}
                    type="text"
                    placeholder={""}
                    className="w-full p-2 border-[1px] rounded-md text-sm"
                  />
                }
              />
              <AIChatbotSettingsRow
                title="URL Filter"
                subtitle="Input URL patterns to filter to in the crawl"
                input={
                  <input
                    value={trainingDetails.crawlUrlFilter || ""}
                    onChange={(e) => {
                      setTrainingDetails({
                        ...trainingDetails!,
                        crawlUrlFilter: e.target.value
                      });
                    }}
                    type="text"
                    placeholder={""}
                    className="w-full p-2 border-[1px] rounded-md text-sm"
                  />
                }
              />
              {subscription.subscription?.enableAdvancedAIFeatures && (
                <AIChatbotSettingsRowToggle
                  title="AI Web Crawling"
                  subtitle="Enable AI Based Web Crawling"
                  input={
                    <Toggle
                      toggled={trainingDetails.aiCrawl}
                      changeToggle={() =>
                        setTrainingDetails({
                          ...trainingDetails!,
                          aiCrawl: !trainingDetails.aiCrawl
                        })
                      }
                    />
                  }
                />
              )}
              {trainingDetails.aiCrawl && showAdvancedFeatures && (
                <AIChatbotSettingsRow
                  title="AI Prompt"
                  subtitle="A prompt of instructions to give to the AI for the Web Crawl"
                  input={
                    <textarea
                      value={trainingDetails.aiCrawlPrompt || ""}
                      onChange={(e) => {
                        setTrainingDetails({
                          ...trainingDetails!,
                          aiCrawlPrompt: e.target.value
                        });
                      }}
                      //type="text"
                      placeholder={""}
                      className="w-full h-32 p-2 border-[1px] rounded-md text-sm"
                    />
                  }
                />
              )}
            </>
          )}
        </div>
        <div className="flex justify-end mt-6 gap-6">
          <PrimaryGrayButton
            label="Cancel"
            onClick={() => props.clearTrainingModal()}
          />
          <PrimaryBlueButton
            label="Crawl"
            onClick={() => runCrawler(trainingDetails)}
            disabled={disabled}
          />
        </div>
      </div>
    </Modal>
  );
}
